import styled, { css } from 'styled-components';

import { ClassEnum, COLOR_SCHEME_OPTIONS } from '@/constants/enum';
import { IMainWidgetSettings, IWidgetColor } from '@/types/components/widget';
import { DayPickerStyled } from './day-picker-styled';
import { getColor } from '@/helpers';

const HeaderStyled = css`
  .${ClassEnum.Header} {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .${ClassEnum.HeaderTitle} {
    font-weight: 600;
    scroll-margin: 80px;
    line-height: 1.5;
  }
  .${ClassEnum.HeaderAdditionalMessage} {
    word-break: break-all;
  }
  .${ClassEnum.HeaderRadio} {
    display: flex;
    justify-content: stretch;
    &.${ClassEnum.HeaderRadio}-vertical {
      flex-direction: column;
    }
    gap: 8px;
  }
  .${ClassEnum.HeaderRadioLabel} {
    display: flex;
    min-height: 100%;
    font-size: 14px;
    line-height: 24px;
    cursor: pointer;
    align-items: center;
    gap: 8px;
    border-style: solid;
    border-width: 2px;
    padding: 8px;
    text-align: center;
  }

  .${ClassEnum.HeaderDropdownContentItem} {
    display: flex;
    align-items: center;
    justify-content: start;
    .${ClassEnum.HeaderDropdownContentItemIcon} {
      margin-right: 8px;
      width: 24px;
      height: 24px;
    }
    .${ClassEnum.HeaderDropdownContentItemText} {
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
    }
  }
`;

const PickupStyled = css`
  .${ClassEnum.Pickup} {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .${ClassEnum.PickupLabel} {
    letter-spacing: 0em;
    font-weight: 600;
    scroll-margin: 80px;
  }

  .${ClassEnum.PickupLocationList} {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    height: 100%;
    max-height: 208px;
    overflow: hidden auto;
    scrollbar-width: thin;

    .${ClassEnum.PickupLocationItem} {
      display: flex;
      gap: 8px;
      padding: 8px;
      border-width: 2px;
      border-style: solid;
      cursor: pointer;
    }
    .${ClassEnum.PickupLocationItemContainer} {
      display: flex;
      flex-direction: column;
    }
    .${ClassEnum.PickupLocationItemTitle} {
      font-size: 16px;
      font-weight: 600;
      line-height: 24px;
    }
    .${ClassEnum.PickupLocationItemDescription} {
      font-size: 14px;
      line-height: 20px;
    }
  }
`;
const DeliveryStyled = css`
  .${ClassEnum.Delivery} {
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition-duration: 200ms;
    animation: fadeIn 200ms ease-in;
  }
  .${ClassEnum.DeliveryLabel} {
    line-height: 16px;
    font-weight: 500;
    scroll-margin: 80px;
  }

  .${ClassEnum.ZipcodeWrapper} {
    display: flex;
    gap: 8px;
  }
  .${ClassEnum.ZipcodeInput} {
    font-size: 14px;
    line-height: 20px;
    padding: 4px 12px;
    border-width: 1px;
    border-style: solid;
    width: 100%;
    height: 36px;
    &:focus-visible {
      outline: none;
      box-shadow: none;
    }
  }
  .${ClassEnum.ZipcodeSubmitButton} {
    width: 100%;
    height: 36px;
    border-width: 1px;
    border-style: solid;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    padding: 8px 16px;
    white-space: nowrap;
    display: inline-flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    /* &:hover {
      background-color: #f5f5f5 !important;
    } */
  }
  .${ClassEnum.DeliveryEligibleMessage} {
    line-height: 20px;
    word-break: break-all;
  }
  .${ClassEnum.DeliveryErrorMessage} {
    line-height: 20px;
    word-break: break-all;
  }
`;

export interface IAppStyledProps {
  widgetSettings: IMainWidgetSettings;
  widgetColors: IWidgetColor;
}

export const AppStyled = styled.div<IAppStyledProps>`
  .${ClassEnum.Main} {
    * {
      box-sizing: border-box;
      border-width: 0;
      border-style: solid;
    }
    width: 100%;
    /* max-width: 448px; */
    max-width: 384px;
    margin-left: auto;
    margin-right: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
    p {
      margin: 0;
    }
  }
  .${ClassEnum.RadioInput} {
    padding: 0;
    min-width: 14px;
    width: 14px;
    height: 14px;
    cursor: pointer;
    outline: 1px solid ${(props) => props.widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? 'white' : 'black'};
    border-radius: 9999px;
    display: flex !important;
    justify-content: center;
    align-items: center;
  }
  .${ClassEnum.Dropdown} {
    position: relative;
  }
  .${ClassEnum.DropdownLayer} {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
  .${ClassEnum.DropdownButton} {
    width: 100%;
    max-height: 100%;
    border-radius: 8px;
    border-width: 1px;
    border-style: solid;
    border-color: #e5e5e5;
    cursor: pointer;
    display: flex;
    gap: 8px;
    justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    white-space: nowrap;
  }
  .${ClassEnum.DropdownLabelContainer} {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .${ClassEnum.DropdownLabel} {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
  }
  .${ClassEnum.DropdownContent} {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    position: absolute;
    top: calc(100% + 4px);
    width: 100%;
    max-height: 384px;
    overflow-y: auto;
    background-color: white;
    border-width: 1px;
    border-style: solid;
    border-color:${(props) => props.widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '' : '#e5e5e5'};
    background-color:${(props) => props.widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK ? '#1a1a1a' : '#ffffff'};
    box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
    scrollbar-width: thin;
    z-index: 2;
  }
  .${ClassEnum.DropdownContentItem} {
    padding: 8px 12px;
    white-space: nowrap;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    &:hover {
      background-color: #f5f5f5;
    }
    margin: 0 4px;
    &:first-child {
      margin-top: 4px;
    }
    &:last-child {
      margin-bottom: 4px;
    }
  }
  .${ClassEnum.DropdownButtonIcon} {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 6px;
    opacity: ${(props) =>
      props.widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK
        ? 1
        : 0.85};
    color: ${(props) =>
      props.widgetSettings.colorScheme === COLOR_SCHEME_OPTIONS.DARK
        ? '#FFFFFF'
        : '#AAAAAA'};
  }

  .${ClassEnum.AdditionalMessage} > p {
    line-height: 20px;
  }

  .${ClassEnum.Disabled} {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .${ClassEnum.TimePickerLabel} {
    font-weight: 600;
  }

  .${ClassEnum.RequiredMessage} {
    color: ${(props) => getColor(props.widgetSettings?.colorScheme, props.widgetColors, 'body', 'errorColor')};
  }

  .${ClassEnum.MinimumOrderAmountError} {
    color: ${(props) => getColor(props.widgetSettings?.colorScheme, props.widgetColors, 'body', 'errorColor')};
  }

  ${HeaderStyled}
  ${PickupStyled}
  ${DeliveryStyled}
  ${(props) => DayPickerStyled(props.widgetSettings, props.widgetColors)}
`;
